import { formatAvailabilityRequestDate } from "./dates.js";
import { maybePluralizeNoun } from "./words.js";
import { isMonthFirst } from "./locale.js";
import { getSiteTypeLabel } from "./labels.js";
import { differenceInDays } from "date-fns";

function includeNightsText(availabilityRequest) {
  // If this gets updated, make sure to update the arguments
  // that are passed in via RequestForm
  // (it doesn't use the whole availabilityRequest object)
  return availabilityRequest.siteType !== "other";
}

function nightsText(availabilityRequest) {
  const nights = availabilityRequest.minStayLength;
  return maybePluralizeNoun(nights, "night");
}

function sitesText(
  availabilityRequest,
  { count, includeSiteType = false, country, locale } = {},
) {
  const sites =
    typeof count === "number" ? count : availabilityRequest._count.sites;

  if (!includeSiteType) {
    return maybePluralizeNoun(sites, "site");
  }

  const typeValue = availabilityRequest.siteType;
  if (!typeValue || typeValue === "other") {
    return maybePluralizeNoun(sites, "site");
  }

  const label = getSiteTypeLabel(typeValue, country);
  // Resolver falls back to the raw enum value on unknown country/type. Guard
  // against emitting "3 rv sites" in lowercase — drop the label entirely.
  if (label === typeValue) {
    return maybePluralizeNoun(sites, "site");
  }

  return maybePluralizeNoun(sites, `${label} site`);
}

function groupConsecutiveDates(dates) {
  if (!dates || dates.length === 0) return [];

  const sortedDates = [...dates].sort((a, b) => a - b);
  const groups = [];
  let currentGroup = [sortedDates[0]];

  for (let i = 1; i < sortedDates.length; i++) {
    const current = sortedDates[i];
    const previous = sortedDates[i - 1];

    // Check if dates are consecutive (difference of 1 day)
    // dates are stored as YYYYMMDD integers
    const currentDate = new Date(
      Math.floor(current / 10000),
      Math.floor((current % 10000) / 100) - 1,
      current % 100,
    );
    const previousDate = new Date(
      Math.floor(previous / 10000),
      Math.floor((previous % 10000) / 100) - 1,
      previous % 100,
    );

    if (differenceInDays(currentDate, previousDate) === 1) {
      currentGroup.push(current);
    } else {
      groups.push(currentGroup);
      currentGroup = [current];
    }
  }

  groups.push(currentGroup);
  return groups;
}

function formatDateGroup(group, { locale } = {}) {
  if (group.length === 1) {
    return formatAvailabilityRequestDate(
      group[0],
      !isMonthFirst(locale) ? "d MMM" : "MMM do",
    );
  } else if (group.length === 2) {
    const startDate = group[0];
    const endDate = group[1];
    const startMonth = Math.floor((startDate % 10000) / 100);
    const endMonth = Math.floor((endDate % 10000) / 100);

    if (!isMonthFirst(locale)) {
      if (startMonth === endMonth) {
        return `${formatAvailabilityRequestDate(startDate, "d")}/${formatAvailabilityRequestDate(endDate, "d MMM")}`;
      } else {
        return `${formatAvailabilityRequestDate(startDate, "d MMM")}-${formatAvailabilityRequestDate(endDate, "d MMM")}`;
      }
    }

    if (startMonth === endMonth) {
      return `${formatAvailabilityRequestDate(startDate, "MMM d")}/${formatAvailabilityRequestDate(endDate, "do")}`;
    } else {
      return `${formatAvailabilityRequestDate(startDate, "MMM d")}-${formatAvailabilityRequestDate(endDate, "MMM d")}`;
    }
  } else {
    const startDate = group[0];
    const endDate = group[group.length - 1];
    const startMonth = Math.floor((startDate % 10000) / 100);
    const endMonth = Math.floor((endDate % 10000) / 100);

    if (!isMonthFirst(locale)) {
      if (startMonth === endMonth) {
        return `${formatAvailabilityRequestDate(startDate, "d")}-${formatAvailabilityRequestDate(endDate, "d MMM")}`;
      } else {
        return `${formatAvailabilityRequestDate(startDate, "d MMM")}-${formatAvailabilityRequestDate(endDate, "d MMM")}`;
      }
    }

    if (startMonth === endMonth) {
      return `${formatAvailabilityRequestDate(startDate, "MMM do")}-${formatAvailabilityRequestDate(endDate, "do")}`;
    } else {
      return `${formatAvailabilityRequestDate(startDate, "MMM do")}-${formatAvailabilityRequestDate(endDate, "MMM do")}`;
    }
  }
}

function arrivalText(availabilityRequest, { locale } = {}) {
  const dates = availabilityRequest.selectedDates || [];
  const dateGroups = groupConsecutiveDates(dates);
  const maxVisibleGroups = 4; // Show more groups in email since we have more space

  const visibleGroups = dateGroups.slice(0, maxVisibleGroups);
  const hiddenGroupsCount = dateGroups.length - maxVisibleGroups;

  let text = visibleGroups
    .map((group) => formatDateGroup(group, { locale }))
    .join(", ");

  if (hiddenGroupsCount > 0) {
    const hiddenDatesCount =
      dates.length -
      visibleGroups.reduce((sum, group) => sum + group.length, 0);
    text += `, and ${hiddenDatesCount} more`;
  }

  return text;
}

function includeCapacityText(availabilityRequest) {
  return availabilityRequest.minCapacity > 1;
}

function getCapacityUnit(facility) {
  if (!facility?.sites) return null;
  const site = facility.sites.find((s) => s.capacityUnit);
  return site?.capacityUnit || "people";
}

function capacityUnitLabel(unit, count) {
  const labels = {
    tent_pads: count === 1 ? "tent pad" : "tent pads",
    people: count === 1 ? "person" : "people",
    seats: count === 1 ? "seat" : "seats",
    sites: count === 1 ? "site" : "sites",
    slots: count === 1 ? "slot" : "slots",
  };
  return labels[unit] || (count === 1 ? "person" : "people");
}

function capacityText(availabilityRequest, { unit } = {}) {
  const capacity = availabilityRequest.minCapacity;
  const label = capacityUnitLabel(unit || "people", capacity);
  return `${capacity} ${label}`;
}

function tripSummaryText(availabilityRequest) {
  return `${availabilityRequest.tripNights.length}-night trip`;
}

function matchStyleText(availabilityRequest) {
  const matchStyle = availabilityRequest.allowPartialMatch
    ? "partial match"
    : "exact match";
  if (!includeCapacityText(availabilityRequest)) return matchStyle;
  const unit = getCapacityUnit(availabilityRequest);
  return `${matchStyle}, ${capacityText(availabilityRequest, { unit })}`;
}

// CAMIS and Mews reserve URL builders used to live here. They now live in
// the booking-strategy modules that own those agencies; we re-export them
// below for callers (including unit tests under utils/) that import them
// by name without knowing about the strategy layer.
export { buildCamisReserveUrl } from "../app/booking/strategies/camis.js";
export { buildMewsReserveUrl } from "../app/booking/strategies/acc.js";

export {
  includeNightsText,
  includeCapacityText,
  nightsText,
  sitesText,
  arrivalText,
  capacityText,
  getCapacityUnit,
  capacityUnitLabel,
  tripSummaryText,
  matchStyleText,
  groupConsecutiveDates,
  formatDateGroup,
};
